import { useMemo } from "react";

import { useFlagOnaAIEnabled, useFlagOnaEnvironmentOnboardingEnabled } from "@/hooks/use-flag";
import {
    Agents,
    useAgentExecutionsForEnvironment,
    useGetAgentExecution,
    type PlainAgentExecution,
} from "@/queries/agent-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { agentExecutionEnvironmentIds, mostRecentAgentExecution } from "@/utils/agent-executions";
import { isSubagentExecution } from "@/utils/subagent";

export type ConversationPanelType =
    | {
          type: "onboarding";
      }
    | { type: "remote-agent-execution"; agentExecution: PlainAgentExecution }
    | { type: "in-environment-agent-execution"; agentExecution?: PlainAgentExecution };

/**
 * Extracts the agent execution from a conversation panel type.
 * Returns null if there's no conversation or no agent execution.
 */
export function getAgentExecution(conversation: ConversationPanelType | null): PlainAgentExecution | null {
    if (!conversation || conversation.type === "onboarding") return null;
    return conversation.agentExecution || null;
}

/**
 * Extracts the first PR URL from an agent execution's outputs.
 * Looks for keys matching `pr_url` or `pr_url_<number>`.
 */
export function getPrUrl(agentExecution: PlainAgentExecution | null): string | undefined {
    if (!agentExecution?.status?.outputs) return undefined;
    for (const key of Object.keys(agentExecution.status.outputs)) {
        if (key === "pr_url" || /^pr_url_\d+$/.test(key)) {
            const output = agentExecution.status.outputs[key];
            if (output?.value?.case === "stringValue" && output.value.value) {
                return output.value.value;
            }
        }
    }
    return undefined;
}

/**
 * Find the most recent non-subagent execution for a given agent ID.
 */
function findExecutionByAgent(
    executions: PlainAgentExecution[] | undefined,
    agentId: string,
): PlainAgentExecution | undefined {
    if (!executions) return undefined;
    const matching = executions.filter((e) => e.spec?.agentId === agentId);
    const nonSubagent = matching.length > 1 ? matching.filter((e) => !isSubagentExecution(e)) : matching;
    return mostRecentAgentExecution(nonSubagent);
}

export const useEnvironmentConversation = (environmentId?: string, preferredAgentExecutionId?: string) => {
    const { data: environment, isPending: isEnvironmentPending } = useEnvironment(environmentId);
    const { data: preferredAgentExecution, isPending: isPreferredAgentExecutionPending } =
        useGetAgentExecution(preferredAgentExecutionId);

    const { data: allExecutions, isPending: isExecutionsPending } = useAgentExecutionsForEnvironment(environmentId);

    const onboardingAgentExecution = useMemo(
        () => findExecutionByAgent(allExecutions, Agents.DevelopmentEnvironmentSetup.id),
        [allExecutions],
    );

    const remoteAgentExecution = useMemo(() => findExecutionByAgent(allExecutions, Agents.Ona.id), [allExecutions]);

    const inEnvironmentAgentExecution = useMemo(
        () => findExecutionByAgent(allExecutions, Agents.InEnvironmentOna.id),
        [allExecutions],
    );

    const inEnvironmentClaudeAgentExecution = useMemo(
        () => findExecutionByAgent(allExecutions, Agents.InEnvironmentClaudeAgent.id),
        [allExecutions],
    );

    const inEnvironmentCodexAgentExecution = useMemo(
        () => findExecutionByAgent(allExecutions, Agents.InEnvironmentCodexAgent.id),
        [allExecutions],
    );

    const inEnvironmentCodexAppAgentExecution = useMemo(
        () => findExecutionByAgent(allExecutions, Agents.InEnvironmentCodexAppAgent.id),
        [allExecutions],
    );

    const { value: isOnaEnvironmentOnboardingEnabled, loading: isOnboardingFlagLoading } =
        useFlagOnaEnvironmentOnboardingEnabled();
    const { value: isOnaEnabled, loading: isOnaFlagLoading } = useFlagOnaAIEnabled();
    const isFeatureFlagEnabled = isOnaEnabled || isOnaEnvironmentOnboardingEnabled;
    const isFlagLoading = isOnboardingFlagLoading || isOnaFlagLoading;

    // New prompt submission already knows the target execution ID. Prefer that
    // optimistic execution on first paint so the details page doesn't flash
    // through the empty conversation state while list queries catch up.
    const preferredConversation = useMemo((): ConversationPanelType | null => {
        if (!preferredAgentExecution || !environmentId) {
            return null;
        }

        if (!agentExecutionEnvironmentIds(preferredAgentExecution).includes(environmentId)) {
            return null;
        }

        if (preferredAgentExecution.spec?.agentId === Agents.Ona.id) {
            return {
                type: "remote-agent-execution",
                agentExecution: preferredAgentExecution,
            };
        }

        // All other agent types (InEnvironmentOna and subagents) are treated
        // as in-environment executions.
        return {
            type: "in-environment-agent-execution",
            agentExecution: preferredAgentExecution,
        };
    }, [preferredAgentExecution, environmentId]);

    // When feature flags have resolved and are disabled, we know the result is
    // null — no need to wait for environment/execution queries.
    const isPending =
        !environmentId || (!isFeatureFlagEnabled && !isFlagLoading)
            ? false
            : isEnvironmentPending ||
              (!!preferredAgentExecutionId && !preferredConversation && isPreferredAgentExecutionPending) ||
              isExecutionsPending;

    const createdFromProject = environment?.metadata?.projectId;
    const showOnboardingConversation =
        isOnaEnvironmentOnboardingEnabled &&
        !isPending &&
        (!createdFromProject || onboardingAgentExecution !== undefined);

    const environmentConversation: ConversationPanelType | null = useMemo(() => {
        if (!isFeatureFlagEnabled || !environmentId) {
            return null;
        }

        if (preferredConversation) {
            return preferredConversation;
        }

        if (isPending) {
            return null;
        }

        if (remoteAgentExecution !== undefined) {
            return {
                type: "remote-agent-execution",
                agentExecution: remoteAgentExecution,
            };
        }

        if (inEnvironmentAgentExecution !== undefined) {
            return {
                type: "in-environment-agent-execution",
                agentExecution: inEnvironmentAgentExecution,
            };
        }

        if (inEnvironmentClaudeAgentExecution !== undefined) {
            return {
                type: "in-environment-agent-execution",
                agentExecution: inEnvironmentClaudeAgentExecution,
            };
        }

        if (inEnvironmentCodexAgentExecution !== undefined) {
            return {
                type: "in-environment-agent-execution",
                agentExecution: inEnvironmentCodexAgentExecution,
            };
        }

        if (inEnvironmentCodexAppAgentExecution !== undefined) {
            return {
                type: "in-environment-agent-execution",
                agentExecution: inEnvironmentCodexAppAgentExecution,
            };
        }

        if (showOnboardingConversation) {
            return {
                type: "onboarding",
            };
        }

        return null;
    }, [
        isFeatureFlagEnabled,
        environmentId,
        preferredConversation,
        isPending,
        remoteAgentExecution,
        inEnvironmentAgentExecution,
        inEnvironmentClaudeAgentExecution,
        inEnvironmentCodexAgentExecution,
        inEnvironmentCodexAppAgentExecution,
        showOnboardingConversation,
    ]);

    return {
        environmentConversation,
        isPending,
    };
};
